/**
 * navigation/rootNavigator.tsx
 * Top-level gate. SK Hire does NOT own authentication — the SK Platform owns
 * the only login / signup / forgot-password / profile screens for every
 * product. So this gate only answers "is there a valid, fully-onboarded
 * platform session?":
 *
 *   • If a legacy verification/reset email landed here (?mode=verify|reset&token=…),
 *     forward the token to the platform first (it owns those flows).
 *   • probe GET /v1/me (shared httpOnly cookie, via the platform API):
 *       200 + profile complete    → app shell
 *       200 + profile INCOMPLETE  → bounce to the platform to finish onboarding
 *       401 / error               → bounce to the centralized platform auth
 *
 * Sign-out clears the session and hands back to the platform auth.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, ActivityIndicator } from 'react-native';

import { useTheme } from '../context/themeContext';
import { getCurrentUser, logoutUser } from '../services/auth/auth';

import AppShell from './appShell';
import {
  redirectToPlatformAuth,
  redirectToPlatformVerify,
  takeVerifyToken,
  redirectToPlatformReset,
  takeResetToken,
  redirectToNotInPlan,
} from './platformAuthRedirect';
import { isNotInPlan } from './planGate';

type Phase = 'booting' | 'app' | 'error';

export default function RootNavigator() {
  const { theme } = useTheme();
  const { colors } = theme;

  const [phase, setPhase] = useState<Phase>('booting');

  const runBoot = useCallback(async () => {
    setPhase('booting');
    try {
      const verifyToken = takeVerifyToken();
      if (verifyToken) { redirectToPlatformVerify(verifyToken); return; }

      const resetToken = takeResetToken();
      if (resetToken) { redirectToPlatformReset(resetToken); return; }

      const me: any = await getCurrentUser();
      const user = me?.data?.user ?? me?.user;
      if (!user) { redirectToPlatformAuth(); return; }
      if (user.profile_complete === false) { redirectToPlatformAuth(); return; }
      // Signed in, but this org's plan does not include the Hire website.
      // The session stays — their own console still calls Hire. No list = open.
      if (isNotInPlan((me?.data ?? me)?.subscribed_products)) { redirectToNotInPlan(); return; }
      setPhase('app');
    } catch (err: any) {
      // A real HTTP response (401/403) → not authenticated → hand off to platform.
      if (err?.response) { redirectToPlatformAuth(); return; }
      // No response → the auth service is unreachable. Offer a retry rather than
      // looping through a redirect that can't succeed.
      setPhase('error');
    }
  }, []);

  useEffect(() => { void runBoot(); }, [runBoot]);

  // Sign out → clear the platform session, then hand back to the platform auth.
  const handleSignOut = async () => {
    try { await logoutUser(); } finally { redirectToPlatformAuth(); }
  };

  if (phase === 'app') {
    return <AppShell onSignOut={handleSignOut} />;
  }

  if (phase === 'error') {
    return (
      <View style={[styles.boot, { backgroundColor: colors.paper }]}>
        <Text style={[styles.errTitle, { color: colors.ink }]}>Can’t reach the sign-in service</Text>
        <Text style={[styles.errBody, { color: colors.ink3 }]}>
          We couldn’t confirm your session — the authentication service didn’t respond.
          This usually means a required backend isn’t running. Your login is fine; try again once it’s back up.
        </Text>
        <Pressable
          onPress={() => { void runBoot(); }}
          style={[styles.retryBtn, { backgroundColor: colors.accent }]}
          accessibilityRole="button"
        >
          <Text style={[styles.retryLabel, { color: colors.paper }]}>Retry</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View style={[styles.boot, { backgroundColor: colors.paper }]}>
      <ActivityIndicator size="large" color={colors.accent} />
    </View>
  );
}

const styles = StyleSheet.create({
  boot: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { height: '100vh' as any } : {}),
  },
  errTitle: { fontSize: 16, fontWeight: '600', marginBottom: 8, textAlign: 'center' },
  errBody: { fontSize: 13, lineHeight: 19, textAlign: 'center', maxWidth: 380, marginBottom: 22, paddingHorizontal: 24 },
  retryBtn: { paddingVertical: 10, paddingHorizontal: 20, borderRadius: 8 },
  retryLabel: { fontSize: 14, fontWeight: '600' },
});
